import "./App.css";

import {
  Admin,
  CREATE,
  CustomRoutes,
  DELETE,
  Login,
  Resource,
  UPDATE,
  useGetIdentity,
} from "react-admin";
import {
  CompaniesList,
  CompanyCreate,
  CompanyEdit,
} from "./modules/companies/companies";
import { LocationCreate, LocationEdit } from "./modules/location/create";
import { OrderCreate, OrderEdit } from "./modules/order/create";
import { UserCreate, UserEdit, UsersList } from "./modules/users/users";
import styled, { createGlobalStyle } from "styled-components";

import { ApolloClient } from "@apollo/client";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";
import BusinessIcon from "@mui/icons-material/Business";
import ChangeMyPassword from "./modules/users/changeMyPassword";
import { CircularProgress } from "@material-ui/core";
import { CompanyResource } from "./modules/companies/fragments";
import CustomLayout from "./CustomLayout";
import CustomTheme from "./CustomTheme";
import { DeliveryNoteResource } from "./modules/deliveryNote/fragments";
import { DeliveryNotesList } from "./modules/deliveryNote/list";
import { LocationsList } from "./modules/location/list";
import LoginForm from "./modules/login";
import { Options } from "@ra-data-prisma/dataprovider";
import { OrderResource } from "./modules/order/fragments";
import { OrdersList } from "./modules/order/list";
import { QueryClient } from "react-query";
import ReorderIcon from "@mui/icons-material/Reorder";
import { Resource as ResourceDataprovider } from "@ra-data-prisma/dataprovider/lib/constants/interfaces";
import { Route } from "react-router-dom";
import { UserResource } from "./modules/users/fragments";
import { appProvidersFactory } from "./appProvidersFactory";
import i18nProvider from "./locales";
import { makePrefixedFullName } from "@ra-data-prisma/dataprovider/lib/utils/makePrefixedFullName";
import schema from "./graphql-schema.json";
import { url } from "src";

export const Center = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
`;

export const SpinnerWrapper = createGlobalStyle`
  html, body, #root {
    height: 100%;
  }
`;

const Title = () => {
  const { identity }: any = useGetIdentity();
  const logo = identity?.selectedCompany?.logo;
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <img
        src="/logo_header.png"
        alt="logo"
        style={{
          flex: 1,
          maxWidth: "100px",
          marginLeft: 0,
          display: "flex",
          alignItems: "center",
        }}
      />
      {logo ? (
        <img
          style={{ marginLeft: 12 }}
          height={35}
          src={`${url}/uploaded_logo/${logo}`}
        />
      ) : null}
    </div>
  );
};

const App: React.VFC<{ client: ApolloClient<unknown> }> = ({ client }) => {
  const options: Options = {
    client,
    introspection: {
      schema: (schema as any).__schema,
    },
    resourceViews: {
      Order: OrderResource,
      DeliveryNote: DeliveryNoteResource,
      Company: CompanyResource,
      User: UserResource,
    },
    queryDialect: "typegraphql",
    mutationOperationNames: {
      typegraphql: {
        [CREATE]: (resource: ResourceDataprovider) =>
          makePrefixedFullName(`createOne${resource.name}`),
        [UPDATE]: (resource: ResourceDataprovider) =>
          makePrefixedFullName(`updateOne${resource.name}`),
        [DELETE]: (resource: ResourceDataprovider) =>
          makePrefixedFullName(`deleteOne${resource.name}`),
      },
    },
  };

  const { dataProvider, authProvider } = appProvidersFactory()(options);

  if (!dataProvider) {
    return (
      <Center>
        <SpinnerWrapper />
        <CircularProgress />
      </Center>
    );
  }

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        staleTime: 1,
      },
    },
  });

  return (
    <Admin
      queryClient={queryClient}
      dataProvider={dataProvider}
      disableTelemetry
      layout={CustomLayout}
      i18nProvider={i18nProvider}
      theme={CustomTheme}
      loginPage={(props) => (
        <Login {...props}>
          <LoginForm />
        </Login>
      )}
      title={<Title />}
      authProvider={authProvider}
    >
      {(permissions) => {
        console.log(permissions);
        if (permissions.deliveryNotesVisitor === true) {
          return [
            <CustomRoutes>
              <Route path="/change-my-password" element={<ChangeMyPassword />} />
            </CustomRoutes>,
            <Resource
              name="DeliveryNote"
              options={{ label: "Dodací listy" }}
              list={DeliveryNotesList}
              icon={BusinessIcon}
            />,
          ];
        }
        return [
          permissions.isAdmin && (
            <Resource
              name="User"
              options={{ label: "Uživatelé" }}
              list={UsersList}
              create={UserCreate}
              edit={UserEdit}
              icon={AssignmentIndIcon}
            />
          ),
          <CustomRoutes>
            <Route path="/change-my-password" element={<ChangeMyPassword />} />
          </CustomRoutes>,
          <Resource
            name="Order"
            options={{ label: "Objednávky" }}
            list={OrdersList}
            create={OrderCreate}
            edit={OrderEdit}
            icon={ReorderIcon}
          />,
          <Resource
            name="DeliveryNote"
            options={{ label: "Dodací listy" }}
            list={DeliveryNotesList}
            icon={BusinessIcon}
          />,
          permissions.isAdmin && (
            <Resource
              name="Company"
              options={{ label: "Firmy" }}
              list={CompaniesList}
              create={CompanyCreate}
              edit={CompanyEdit}
              icon={BusinessIcon}
            />
          ),
          !permissions?.selectedCompany?.isBlocked && (
            <Resource
              name="Location"
              options={{ label: "Adresy" }}
              list={LocationsList}
              create={LocationCreate}
              edit={LocationEdit}
              icon={BusinessIcon}
            />
          ),
          <Resource name="Partner" />,
          <Resource name="LoadingUnit" />,
          <Resource name="CompanyDefaultValue" />,
          <Resource name="UserRelCompany" />,
          <Resource name="DeliveryNoteLoadingUnit" />,
          <Resource name="Caveat" />,
        ];
      }}
    </Admin>
  );
};

export default App;
